import { useEffect, useState } from "react";
import { api, formatNaira, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Trash2, Printer, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exports";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [active, setActive] = useState(null);

  const empty = () => ({
    contact_id: "", business_unit: "FigureWeb", tax_rate: 7.5, notes: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 14 * 86400e3).toISOString().slice(0, 10),
    items: [{ _uid: `li_${Date.now()}`, description: "", quantity: 1, rate: 0 }]
  });
  const [form, setForm] = useState(empty());

  const load = () => api.get("/invoices").then(r => setInvoices(r.data));
  useEffect(() => {
    load();
    api.get("/contacts").then(r => setContacts(r.data));
  }, []);

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) * Number(it.rate)), 0);
  const taxAmt = subtotal * (Number(form.tax_rate) / 100);
  const total = subtotal + taxAmt;

  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === "description" ? val : Number(val) };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { _uid: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, description: "", quantity: 1, rate: 0 }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const create = async () => {
    const c = contacts.find(x => x.id === form.contact_id);
    if (!c) return toast.error("Select a contact");
    await api.post("/invoices", {
      ...form,
      contact_name: c.name, company: c.company, business_unit: c.business_unit,
    });
    toast.success("Invoice created");
    setOpen(false);
    setForm(empty());
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  const markPaid = async (id) => {
    await api.patch(`/invoices/${id}`, { status: "paid" });
    toast.success("Marked as paid");
    load();
    if (active?.id === id) setActive({ ...active, status: "paid" });
  };

  const totals = {
    total: invoices.reduce((s, i) => s + Number(i.total || 0), 0),
    paid: invoices.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.total || 0), 0),
    overdue: invoices.filter(i => i.status === "overdue").reduce((s, i) => s + Number(i.total || 0), 0),
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        subtitle="Create, track, and print invoices in Naira (₦)"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("invoices")} data-testid="export-invoices-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                <Plus className="w-4 h-4 mr-1" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Invoice</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Bill to (Contact)</Label>
                    <Select value={form.contact_id} onValueChange={v => setForm({...form, contact_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.company}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label>Business Unit</Label>
                    <Select value={form.business_unit} onValueChange={v => setForm({...form, business_unit: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                        <SelectItem value="FigureCX">FigureCX</SelectItem>
                        <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Issue date</Label><Input type="date" value={form.issue_date} onChange={e => setForm({...form, issue_date: e.target.value})} /></div>
                  <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
                </div>

                <div>
                  <Label>Line items</Label>
                  <div className="border border-slate-200 rounded-md overflow-hidden mt-1">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">Description</th>
                          <th className="text-right px-3 py-2 w-20">Qty</th>
                          <th className="text-right px-3 py-2 w-32">Rate (₦)</th>
                          <th className="text-right px-3 py-2 w-32">Amount</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.items.map((it, i) => (
                          <tr key={it._uid || `li-${i}`} className="border-t border-slate-100">
                            <td className="px-2 py-1"><Input value={it.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Description" /></td>
                            <td className="px-2 py-1"><Input type="number" value={it.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} className="text-right" /></td>
                            <td className="px-2 py-1"><Input type="number" value={it.rate} onChange={e => updateItem(i, "rate", e.target.value)} className="text-right" /></td>
                            <td className="px-3 py-2 text-right num">{formatNaira(it.quantity * it.rate)}</td>
                            <td className="px-1"><button onClick={() => rmItem(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Button variant="outline" onClick={addItem} className="mt-2" size="sm" data-testid="add-line-item">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tax rate (%)</Label><Input type="number" step="0.5" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: Number(e.target.value)})} /></div>
                  <div className="bg-slate-50 rounded-md p-3 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="num">{formatNaira(subtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">VAT ({form.tax_rate}%)</span><span className="num">{formatNaira(taxAmt)}</span></div>
                    <div className="flex justify-between mt-1 pt-1 border-t border-slate-200 font-heading font-semibold text-slate-900"><span>Total</span><span className="num">{formatNaira(total)}</span></div>
                  </div>
                </div>

                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} data-testid="save-invoice-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Save invoice</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Total invoiced</div><div className="font-heading font-bold text-2xl num">{formatNaira(totals.total)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Paid</div><div className="font-heading font-bold text-2xl num text-emerald-600">{formatNaira(totals.paid)}</div></div>
        <div className="kpi-card"><div className="text-xs uppercase tracking-wider text-slate-500">Overdue</div><div className="font-heading font-bold text-2xl num text-red-600">{formatNaira(totals.overdue)}</div></div>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="table-head">
            <tr>
              <th className="text-left px-4 py-3">Invoice #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Unit</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(i => (
              <tr key={i.id} className="row" data-testid={`invoice-row-${i.id}`}>
                <td className="px-4 py-3 font-medium num text-slate-800">{i.invoice_number}</td>
                <td className="px-4 py-3 text-slate-600">{i.contact_name}<div className="text-xs text-slate-400">{i.company}</div></td>
                <td className="px-4 py-3"><Chip>{i.business_unit}</Chip></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(i.due_date)}</td>
                <td className="px-4 py-3 text-right font-heading font-semibold num">{formatNaira(i.total)}</td>
                <td className="px-4 py-3"><Chip variant={statusChip(i.status)}>{i.status}</Chip></td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button onClick={() => { setActive(i); setViewOpen(true); }} className="text-slate-500 hover:text-brand-aqua-hover p-1" data-testid={`view-invoice-${i.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  {i.status !== "paid" && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(i.id)} className="text-xs h-7 px-2" data-testid={`pay-invoice-${i.id}`}>Mark paid</Button>
                  )}
                  <button onClick={() => del(i.id)} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Preview / print */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <div className="flex justify-end no-print">
                <Button onClick={async () => {
                  try {
                    const res = await api.get(`/invoices/${active.id}/pdf`, { responseType: "blob" });
                    const blob = new Blob([res.data], { type: "application/pdf" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${active.invoice_number}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("PDF downloaded");
                  } catch (e) { toast.error("Failed to download PDF"); }
                }} className="mr-2 bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-1" /> Download PDF
                </Button>
                <Button onClick={() => window.print()} variant="outline" className="mr-2" data-testid="print-invoice-btn">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
              </div>
              <div id="printable-invoice" className="bg-white p-8 rounded-md">
                <div className="flex justify-between items-start border-b border-slate-200 pb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <img src="/assets/figuraid-logo.png" alt="Figuraid" className="w-14 h-14 object-contain" />
                      <div className="font-heading font-bold text-xl">Figuraid Ltd</div>
                    </div>
                    <div className="text-xs text-slate-500 leading-relaxed">
                      72/73 Ralph Shodeinde Street<br/>Central Business District, Abuja<br/>hello@figuraid.com  ·  +234 800 000 0000
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-2xl text-slate-900">INVOICE</div>
                    <div className="text-sm num text-slate-600">{active.invoice_number}</div>
                    <div className="mt-2"><Chip variant={statusChip(active.status)}>{active.status}</Chip></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bill to</div>
                    <div className="font-medium text-slate-800">{active.contact_name}</div>
                    <div className="text-slate-600">{active.company}</div>
                    <div className="text-slate-500 mt-1">Unit: {active.business_unit}</div>
                  </div>
                  <div className="text-right">
                    <div><span className="text-slate-500">Issue date:</span> <span className="text-slate-800">{formatDate(active.issue_date)}</span></div>
                    <div><span className="text-slate-500">Due date:</span> <span className="text-slate-800">{formatDate(active.due_date)}</span></div>
                  </div>
                </div>

                <table className="w-full text-sm mt-6 border-t border-b border-slate-200">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-right px-3 py-2 w-16">Qty</th>
                      <th className="text-right px-3 py-2 w-28">Rate</th>
                      <th className="text-right px-3 py-2 w-32">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(active.items || []).map((it, i) => (
                      <tr key={it._uid || `${it.description}-${i}`} className="border-t border-slate-100">
                        <td className="px-3 py-2">{it.description}</td>
                        <td className="px-3 py-2 text-right num">{it.quantity}</td>
                        <td className="px-3 py-2 text-right num">{formatNaira(it.rate)}</td>
                        <td className="px-3 py-2 text-right num">{formatNaira(it.quantity * it.rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="flex justify-end mt-4">
                  <div className="w-64 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="num">{formatNaira(active.subtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">VAT ({active.tax_rate}%)</span><span className="num">{formatNaira(active.tax_amount)}</span></div>
                    <div className="flex justify-between font-heading font-bold text-lg text-slate-900 border-t border-slate-200 pt-2 mt-1">
                      <span>Total</span><span className="num">{formatNaira(active.total)}</span>
                    </div>
                  </div>
                </div>

                {active.notes && (
                  <div className="mt-8 text-xs text-slate-500 border-t border-slate-100 pt-4">
                    <div className="uppercase tracking-wider font-medium mb-1">Notes</div>
                    <div>{active.notes}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
